import { useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { summarize } from '../features/finance/calc';
import { inPeriod, type Period } from '../lib/period';
import { PeriodToggle } from '../components/PeriodToggle';
import { TitleBar } from '../components/TitleBar';
import { ProfitTiles } from '../features/finance/FinanceTiles';
import {
  CustomersOwePanel,
  NeedsAttentionPanel,
  ProfitByModePanel,
  ProfitChartPanel,
  SerbOwesPanel,
  ShipmentSummaryPanel,
} from '../features/finance/FinancePanels';
import { useFinance } from '../features/finance/useFinance';
import { longDate } from '../lib/dates';
import { jordanDate } from '@serb/shared';

const GAP = { xs: 1.5, md: 2 };
const FIT = {
  display: 'grid',
  gap: GAP,
  alignItems: 'start',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))',
};

/** SERB's money across all shipments: what came in, what went out, what is owed each way, and what needs you */
export function FinancePage() {
  const [period, setPeriod] = useState<Period>('This Year');
  const all = useFinance();
  const fins = all.filter((f) => inPeriod(f.date, period));
  const s = summarize(fins);

  return (
    <Stack spacing={GAP}>
      <TitleBar
        title="Financial Overview"
        subtitle="A complete view of SERB's financial performance across all shipments, customers, agents, and carriers."
      >
        <Typography variant="body2" color="text.secondary">
          {longDate(jordanDate())}
        </Typography>
        <PeriodToggle value={period} onChange={setPeriod} />
      </TitleBar>

      <ProfitTiles s={s} />

      <Box sx={FIT}>
        <ProfitChartPanel fins={all} />
        <ProfitByModePanel fins={fins} />
        <NeedsAttentionPanel fins={all} />
      </Box>
      <ShipmentSummaryPanel fins={fins} limit={5} />
      <Box sx={FIT}>
        <CustomersOwePanel fins={all} />
        <SerbOwesPanel fins={all} />
      </Box>
    </Stack>
  );
}
